import { useState } from "react";
import { useMutation } from "@connectrpc/connect-query";

import {
  createBillingPortalSession,
  createCheckoutSession,
} from "@/gen/nodebase/v1/billing-BillingService_connectquery";
import {
  WorkerAccess,
  type GetBillingStatusResponse,
} from "@/gen/nodebase/v1/billing_pb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ErrorNote } from "@/components/page";
import { errorReason } from "@/lib/format";

// Worker entitlement on the Worker page (docs/paid-worker-signup.md). Payment
// itself happens on the processor's hosted page; this only sends the owner
// there and back. Copy approved in business-ops#9 (2026-11-02); the Terms
// (apps/site/terms.html §07) say the same things at length.

export function accessRunsWorker(access: WorkerAccess | undefined): boolean {
  return (
    access === WorkerAccess.INCLUDED ||
    access === WorkerAccess.SUBSCRIBED ||
    access === WorkerAccess.PAST_DUE
  );
}

function day(unix: bigint): string {
  return new Date(Number(unix) * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const regionNames: Record<string, string> = { us: "the US", jp: "Japan" };

function useHostedPage() {
  const checkout = useMutation(createCheckoutSession);
  const portal = useMutation(createBillingPortalSession);
  const [error, setError] = useState<unknown>(null);
  const go = async (open: () => Promise<{ url: string }>) => {
    setError(null);
    try {
      const { url } = await open();
      window.location.assign(url);
    } catch (err) {
      setError(err);
    }
  };
  return {
    error,
    busy: checkout.isPending || portal.isPending,
    subscribe: () => go(() => checkout.mutateAsync({})),
    manage: () => go(() => portal.mutateAsync({})),
  };
}

// SubscribeCard stands where "Set up my Worker" would, for an owner whose
// Workspace is not entitled to one.
export function SubscribeCard({
  status,
  onInterest,
}: {
  status: GetBillingStatusResponse;
  onInterest: () => void;
}) {
  const hosted = useHostedPage();
  const where = regionNames[status.workerRegion] ?? status.workerRegion.toUpperCase();
  const full = errorReason(hosted.error) === "paid-capacity-full";
  const waiting = status.interestRecorded || full;
  const lapsed = status.access === WorkerAccess.LAPSED;

  let body: React.ReactNode;
  if (!status.billingEnabled) {
    body = (
      <p className="text-sm text-muted-foreground">
        Workers are invitation-only right now. If you have an invite code,
        contact us and we will add it to your account.
      </p>
    );
  } else if (!status.slotAvailable || waiting) {
    body = (
      <>
        <p className="text-sm text-muted-foreground">
          Every Worker place in {where} is taken right now.{" "}
          {waiting
            ? "You're on the waiting list. We'll email you when a place opens."
            : "Join the waiting list and we'll email you as soon as one opens — you won't be charged unless you subscribe."}
        </p>
        {!waiting && (
          <Button
            disabled={hosted.busy}
            onClick={async () => {
              await hosted.subscribe();
              onInterest();
            }}
          >
            Notify me
          </Button>
        )}
      </>
    );
  } else {
    body = (
      <>
        <p className="text-sm text-muted-foreground">
          Your own always-on Worker in {where}: 1 vCPU, 1 GB memory and 20 GB
          of persistent storage. {status.priceLabel} plus applicable tax.
          Cancel anytime; it runs until the end of the month you paid for.
        </p>
        <Button disabled={hosted.busy} onClick={() => void hosted.subscribe()}>
          {hosted.busy ? "Opening payment…" : `Subscribe — ${status.priceLabel}`}
        </Button>
        <p data-testid="subscribe-terms" className="text-xs text-muted-foreground">
          By subscribing you agree to the{" "}
          <a className="underline" href="https://www.nodebase.com/terms.html#early-access" target="_blank" rel="noreferrer">
            Terms of Service
          </a>
          . Your subscription renews monthly until you cancel under Manage
          subscription, and your first payment is refundable within 14 days.
          Payment is handled by Stripe and isn't available from some countries,
          including mainland China. Nodebase is operated by Teahood Inc.,
          Delaware, USA.
        </p>
      </>
    );
  }

  return (
    <Card data-testid="subscribe-card">
      <CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium">
            {lapsed ? "Your subscription has ended" : "Start your Worker"}
          </p>
          {status.testMode && <Badge variant="outline">Test mode — no real charge</Badge>}
        </div>
        {!full && <ErrorNote error={hosted.error} />}
        {body}
        {status.manageAvailable && (
          <Button variant="ghost" disabled={hosted.busy} onClick={() => void hosted.manage()}>
            Billing history
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// SubscriptionNote sits above an entitled owner's Worker: renewal, a failed
// payment's deadline, or a scheduled cancellation. Free grants show nothing.
export function SubscriptionNote({ status }: { status: GetBillingStatusResponse }) {
  const hosted = useHostedPage();
  if (status.access === WorkerAccess.INCLUDED || status.access === WorkerAccess.REQUIRED) {
    return null;
  }
  const pastDue = status.access === WorkerAccess.PAST_DUE;
  const lapsed = status.access === WorkerAccess.LAPSED;
  let text: string;
  if (pastDue) {
    text = `Your last payment failed. Update your payment method by ${day(status.graceUntilUnix)} to keep your Worker.`;
  } else if (lapsed) {
    text = "Your subscription has ended. We keep your Worker's data for 30 days after that. To get it back, subscribe again and email support@nodebase.com — we'll restore it to your new Worker.";
  } else if (status.cancelAtPeriodEnd) {
    text = `Your subscription ends on ${day(status.currentPeriodEndUnix)}. Your Worker keeps running until then.`;
  } else {
    text = `Subscribed — ${status.priceLabel}, renews ${day(status.currentPeriodEndUnix)}.`;
  }
  return (
    <div
      data-testid="subscription-note"
      className={`mb-4 flex flex-wrap items-center gap-3 rounded-md border px-4 py-3 text-sm ${pastDue || lapsed ? "nb-alert nb-alert--danger" : "bg-secondary"}`}
    >
      <p className="flex-1">{text}</p>
      {lapsed ? (
        <Button size="sm" disabled={hosted.busy} onClick={() => void hosted.subscribe()}>
          Subscribe again
        </Button>
      ) : (
        status.manageAvailable && (
          <Button size="sm" variant="outline" disabled={hosted.busy} onClick={() => void hosted.manage()}>
            {pastDue ? "Update payment" : "Manage subscription"}
          </Button>
        )
      )}
      <ErrorNote error={hosted.error} />
    </div>
  );
}
